import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Calendar, Check, CheckCircle2, Loader2, Lock } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { formsApi, type FormAnswers, type FormResponse, type FormResponseAttachment } from '@/lib/api';
import { FormRenderer } from '@/components/forms/FormRenderer';
import { formatDateShort, formatDueDate, validateAnswersClient } from '@/components/forms/form-utils';

type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

export function FormFill() {
  const { formId, responseId } = useParams<{ formId: string; responseId: string }>();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['formResponse', formId, responseId],
    queryFn: () => formsApi.getResponse(formId!, responseId!),
    enabled: !!formId && !!responseId,
  });

  const [answers, setAnswers] = useState<FormAnswers>({});
  const [attachments, setAttachments] = useState<FormResponseAttachment[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const hydrated = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef<FormAnswers>({});

  // Hydrate local state once from the server copy.
  useEffect(() => {
    if (data && !hydrated.current) {
      setAnswers(data.answers || {});
      latest.current = data.answers || {};
      setAttachments(data.attachments || []);
      hydrated.current = true;
    }
  }, [data]);

  const fields = data?.effectiveFields ?? data?.form?.fields ?? [];
  const locked = data?.status === 'submitted';
  const form = data?.form;

  const save = useCallback(
    async (toSave: FormAnswers) => {
      if (!formId || !responseId) return;
      setSaveState('saving');
      try {
        await formsApi.saveAnswers(formId, responseId, toSave);
        setSaveState('saved');
      } catch {
        setSaveState('error');
      }
    },
    [formId, responseId],
  );

  const scheduleSave = useCallback(
    (next: FormAnswers) => {
      latest.current = next;
      setSaveState('dirty');
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => save(latest.current), 600);
    },
    [save],
  );

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const onChange = (fieldId: string, value: unknown) => {
    // `latest` is the source of truth so a fast sequence of edits never saves a stale copy.
    const next = { ...latest.current };
    if (value === undefined) delete next[fieldId];
    else next[fieldId] = value;
    setAnswers(next);
    scheduleSave(next);
    if (errors[fieldId]) setErrors((e) => ({ ...e, [fieldId]: '' }));
  };

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (timer.current) clearTimeout(timer.current);
      return formsApi.submit(formId!, responseId!, latest.current);
    },
    onSuccess: (res: FormResponse) => {
      queryClient.setQueryData(['formResponse', formId, responseId], (old: FormResponse | undefined) => ({ ...(old as FormResponse), ...res }));
      queryClient.invalidateQueries({ queryKey: ['myForms'] });
      queryClient.invalidateQueries({ queryKey: ['projectForms'] });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    onError: (err: any) => {
      const serverErrors: Array<{ fieldId: string; message: string }> = err?.body?.errors || [];
      if (serverErrors.length) {
        setErrors(Object.fromEntries(serverErrors.map((e) => [e.fieldId, e.message])));
        setSubmitError('Please fix the highlighted answers.');
      } else {
        setSubmitError(err?.message || "Couldn't submit the form");
      }
    },
  });

  const handleSubmit = () => {
    setSubmitError(null);
    const clientErrors = validateAnswersClient(fields, latest.current);
    if (clientErrors.length) {
      setErrors(Object.fromEntries(clientErrors.map((e) => [e.fieldId, e.message])));
      setSubmitError('Please fix the highlighted answers.');
      const first = document.getElementById(`field-${clientErrors[0].fieldId}`);
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setErrors({});
    submitMutation.mutate();
  };

  const backHref = useMemo(() => (form?.projectId ? `/projects/${form.projectId}/forms` : '/forms'), [form?.projectId]);
  const due = data?.dueDate ? formatDueDate(data.dueDate) : null;
  const questionCount = fields.filter((f) => f.type !== 'section').length;
  const answeredCount = fields.filter((f) => f.type !== 'section' && answers[f.id] !== undefined && answers[f.id] !== '' && !(Array.isArray(answers[f.id]) && (answers[f.id] as unknown[]).length === 0)).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-grey-400 animate-spin" />
      </div>
    );
  }
  if (isError || !data || !form) {
    return (
      <div className="max-w-3xl mx-auto">
        <Card>
          <p className="text-secondary font-medium">Couldn't load this form</p>
          <p className="text-sm text-grey-500 mt-1">{(error as any)?.message || 'It may have been removed, or you may not have access.'}</p>
          <div className="mt-4 flex gap-2">
            <Button variant="outline" onClick={() => refetch()}>Retry</Button>
            <Link to="/forms" className="btn-ghost">Back to forms</Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <Link to={backHref} className="inline-flex items-center gap-1 text-sm text-grey-500 hover:text-secondary mb-4">
        <ArrowLeft className="w-4 h-4" /> {form.project?.name ? `${form.project.name} forms` : 'My forms'}
      </Link>

      <Card>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
          <div>
            <h1 className="text-heading-2 text-secondary">{form.title}</h1>
            {form.description && <p className="text-sm text-grey-600 mt-2 whitespace-pre-line">{form.description}</p>}
          </div>
          {!locked && (
            <span className="text-xs text-grey-400 shrink-0 tabular-nums">
              {answeredCount}/{questionCount} answered
            </span>
          )}
        </div>

        {(data.note || due || data.assignedBy) && !locked && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-grey-500 mt-3">
            {data.assignedBy && <span>Requested by {data.assignedBy.name || data.assignedBy.email}</span>}
            {due && (
              <span className={cn('inline-flex items-center gap-1', due.isOverdue ? 'text-error font-medium' : due.isDueSoon ? 'text-warning' : '')}>
                <Calendar className="w-3 h-3" /> {due.text}
              </span>
            )}
            {data.note && <span className="basis-full text-grey-600 whitespace-pre-line">{data.note}</span>}
          </div>
        )}

        {locked && (
          <div className="mt-4 p-3 rounded-lg bg-success/10 border border-success/20 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-success shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-secondary">Submitted {formatDateShort(data.submittedAt)}</p>
              <p className="text-grey-600 mt-0.5">
                Your answers are locked. If something needs to change, ask {data.assignedBy?.name || 'the Solid Digital team'} to reopen it.
              </p>
            </div>
          </div>
        )}

        {data.reopenedAt && !locked && (
          <div className="mt-4 p-3 rounded-lg bg-warning/10 border border-warning/20 text-sm text-secondary">
            This response was reopened on {formatDateShort(data.reopenedAt)} — make your changes and submit again.
          </div>
        )}

        <div className="mt-6">
          <FormRenderer
            fields={fields}
            answers={answers}
            onChange={onChange}
            readOnly={locked}
            errors={errors}
            upload={{ organizationId: form.organizationId, responseId: data.id, attachments, onAttachmentsChange: setAttachments }}
          />
        </div>

        {!locked && (
          <div className="mt-8 pt-5 border-t border-grey-100 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
            <SaveIndicator state={saveState} onRetry={() => save(latest.current)} />
            <div className="flex items-center gap-3">
              {submitError && (
                <p className="text-sm text-error" role="alert">
                  {submitError}
                </p>
              )}
              <Button onClick={handleSubmit} isLoading={submitMutation.isPending}>
                <Lock className="w-4 h-4 mr-2" />
                Submit
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function SaveIndicator({ state, onRetry }: { state: SaveState; onRetry: () => void }) {
  if (state === 'idle') return <span className="text-xs text-grey-400">Your progress saves automatically.</span>;
  if (state === 'saving' || state === 'dirty')
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-grey-400">
        <Loader2 className="w-3 h-3 animate-spin" /> Saving…
      </span>
    );
  if (state === 'saved')
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-grey-500">
        <Check className="w-3 h-3 text-success" /> Draft saved
      </span>
    );
  return (
    <span className="inline-flex items-center gap-2 text-xs text-error">
      Couldn't save your draft.
      <button type="button" onClick={onRetry} className="underline">
        Retry
      </button>
    </span>
  );
}
